import { BreakpointObserver } from '@angular/cdk/layout';
import { computed, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';

export type EmailPanel = 'list' | 'details' | 'preview';
export type EmailLayoutMode = 'single' | 'double' | 'triple';

@Injectable({
  providedIn: 'root',
})
export class EmailLayoutService {
  private _bpObserver = inject(BreakpointObserver);

  private isWideLayout$ = this._bpObserver
    .observe(['(min-width: 1440px)'])
    .pipe(map(result => result.matches));

  private isUltraWideLayout$ = this._bpObserver
    .observe(['(min-width: 1920px)'])
    .pipe(map(result => result.matches));

  readonly isWideLayout = toSignal(this.isWideLayout$, { initialValue: false });
  readonly isUltraWideLayout = toSignal(this.isUltraWideLayout$, { initialValue: false });

  readonly layoutMode = computed<EmailLayoutMode>(() => {
    if (this.isUltraWideLayout()) return 'triple';
    if (this.isWideLayout()) return 'double';
    return 'single';
  });

  readonly activePanel = signal<EmailPanel>('list');

  openList(): void {
    this.activePanel.set('list');
  }

  openDetails(): void {
    this.activePanel.set('details');
  }

  openPreview(): void {
    this.activePanel.set('preview');
  }

  readonly showList = computed(() => {
    const layoutMode = this.layoutMode();
    const panel = this.activePanel();

    if (layoutMode === 'single') return panel === 'list';
    if (layoutMode === 'double') return panel !== 'preview';
    return true;
  });

  readonly showDetails = computed(() => {
    const layoutMode = this.layoutMode();
    const panel = this.activePanel();

    if (layoutMode === 'single') return panel === 'details';
    return panel === 'details' || panel === 'preview';
  });

  readonly showPreview = computed(() => {
    const panel = this.activePanel();

    return panel === 'preview';
  });
}
